import {
  JobRoleStatus,
  JobRoleVersionStatus,
  normalizeJobRoleContent,
  type EmployeeJobRoleAssignment,
  type JobRoleAuditItem,
  type JobRoleContent,
  type JobRoleDetail,
  type JobRoleEmployee,
  type JobRoleListItem,
  type JobRoleTemplateDetail,
  type JobRoleTemplateListItem,
  type JobRoleVersionDetail,
  type JobRoleVersionItem,
  type Paged,
  type BenchmarkGeography,
  type BenchmarkGeographyType,
  type BenchmarkLicenseStatus,
  type BenchmarkResult,
  type BenchmarkSourceKind,
  type BenchmarkSourceRegistryItem,
  type BenchmarkSourceResult,
  type BenchmarkStatus,
  type EmployerPayBandSummary,
  type PayBand,
  type PayBasis,
  type CreateReferenceOptionResult,
  type JobRoleCapabilities,
  type ListReferenceOptionsParams,
  type OccupationDataset,
  type OccupationItem,
  type OccupationSearchParams,
  type OccupationSearchResult,
  type OccupationSystem,
  type ReferenceCategory,
  type ReferenceOption,
  type ReferenceOptionList,
  type RoleCodeSuggestion,
  type RoleCodeSuggestionParams,
} from './types';

/**
 * The seam between the REAL Roles API wire shapes (pinned by __fixtures__ and JobRoleWireContractTests on the API side) and the
 * UI types. Status enums travel as NAMES; every other difference (optional members, draft versus current content, date-only values)
 * is absorbed here, so no screen ever branches on a missing member. Nothing here invents data: a member the server did not send
 * becomes null, an empty list or undefined, never a guess.
 */

const STATUS_BY_NAME: Record<string, number> = { draft: JobRoleStatus.Draft, active: JobRoleStatus.Active, archived: JobRoleStatus.Archived };
const VERSION_STATUS_BY_NAME: Record<string, number> = {
  draft: JobRoleVersionStatus.Draft, published: JobRoleVersionStatus.Published, superseded: JobRoleVersionStatus.Superseded,
};

/** An unknown name maps to 0, which no label map knows, so the screen shows "Unknown" rather than a wrong status. */
export const jobRoleStatusFromWire = (name: string | null | undefined): number => STATUS_BY_NAME[(name ?? '').toLowerCase()] ?? 0;
export const jobRoleVersionStatusFromWire = (name: string | null | undefined): number => VERSION_STATUS_BY_NAME[(name ?? '').toLowerCase()] ?? 0;
export const jobRoleStatusToWire = (status: number): string => ['', 'Draft', 'Active', 'Archived'][status] ?? '';

// ---- raw wire shapes (what the API actually sends) ----

interface RawContent extends Partial<Omit<JobRoleContent, 'qualifications' | 'employmentGuidance'>> {
  qualifications?: Partial<JobRoleContent['qualifications']> | null;
  employmentGuidance?: Partial<JobRoleContent['employmentGuidance']> | null;
}

const content = (raw: RawContent | null | undefined): JobRoleContent => normalizeJobRoleContent(raw as Partial<JobRoleContent> | null | undefined);

export interface RawPayBandSummary {
  basis: string; minimum: number; midpoint: number; maximum: number; annualHoursAssumption: number; concurrencyVersion: number;
}

export interface RawRoleListItem {
  id: string; roleCode: string | null; title: string; departmentId: string | null; departmentName: string | null; jobFamily: string | null;
  careerLevel: string | null; status: string; effectiveFromUtc: string | null; currentVersionNumber: number | null; employeesAssigned: number;
  typicalPayType: string | null; concurrencyVersion: number;
  /** PR 2. A response from before PR 2 has neither member. */
  employerPayBand?: RawPayBandSummary | null; benchmarkStatus?: string | null;
}

export interface RawRoleDetail {
  id: string; roleCode: string | null; title: string; description: string | null; departmentId: string | null; departmentName: string | null;
  jobFamily: string | null; careerLevel: string | null; managementLevel: string | null; socCode: string | null; onetCode: string | null;
  status: string; sourceTemplateId: string | null; effectiveFromUtc: string | null; currentVersionNumber: number | null; hasDraft: boolean;
  draftVersionNumber: number | null; concurrencyVersion: number; employeesAssigned: number; typicalPayType: string | null;
  createdAtUtc: string; updatedAtUtc: string | null; contentVersionNumber: number | null; contentVersionStatus: string | null;
  content: RawContent | null; draftContent: RawContent | null;
  employerPayBand?: RawPayBandSummary | null; benchmarkStatus?: string | null;
}

export interface RawVersion {
  id: string; versionNumber: number; status: string; effectiveFromUtc: string | null; effectiveToUtc: string | null;
  createdByUserId: string | null; createdAtUtc: string | null; publishedAtUtc: string | null; contentSchemaVersion?: number; content?: RawContent | null;
}

export interface RawAssignment {
  id: string; jobTitleId: string; roleTitle: string; roleCode: string | null; roleStatus: string; jobRoleVersionId: string | null;
  versionNumber: number | null; isPrimary: boolean; effectiveFrom: string; effectiveTo: string | null; assignedByUserId: string | null; reason: string | null;
}

export interface RawTemplateDetail extends JobRoleTemplateListItem {
  sourceNote?: string | null;
  content: RawContent | null;
}

// ---- mappers ----

export function mapRoleListItem(raw: RawRoleListItem): JobRoleListItem {
  return {
    id: raw.id, roleCode: raw.roleCode ?? null, title: raw.title, departmentId: raw.departmentId ?? null, departmentName: raw.departmentName ?? null,
    jobFamily: raw.jobFamily ?? null, careerLevel: raw.careerLevel ?? null, status: jobRoleStatusFromWire(raw.status),
    effectiveFromUtc: raw.effectiveFromUtc ?? null, currentVersionNumber: raw.currentVersionNumber ?? null, employeesAssigned: raw.employeesAssigned ?? 0,
    typicalPayType: raw.typicalPayType ?? null, concurrencyVersion: raw.concurrencyVersion,
    employerPayBand: mapPayBandSummary(raw.employerPayBand), benchmarkStatus: benchmarkStatusFromWire(raw.benchmarkStatus),
  };
}

export function mapRolePage(raw: { items: RawRoleListItem[]; page: number; pageSize: number; totalCount: number }): Paged<JobRoleListItem> {
  return { items: (raw.items ?? []).map(mapRoleListItem), page: raw.page, pageSize: raw.pageSize, totalCount: raw.totalCount };
}

export function mapRoleDetail(raw: RawRoleDetail): JobRoleDetail {
  return {
    id: raw.id, roleCode: raw.roleCode ?? null, title: raw.title, description: raw.description ?? null, departmentId: raw.departmentId ?? null,
    departmentName: raw.departmentName ?? null, jobFamily: raw.jobFamily ?? null, careerLevel: raw.careerLevel ?? null,
    managementLevel: raw.managementLevel ?? null, socCode: raw.socCode ?? null, onetCode: raw.onetCode ?? null,
    status: jobRoleStatusFromWire(raw.status), effectiveFromUtc: raw.effectiveFromUtc ?? null, currentVersionNumber: raw.currentVersionNumber ?? null,
    sourceTemplateId: raw.sourceTemplateId ?? null, concurrencyVersion: raw.concurrencyVersion, hasDraft: Boolean(raw.hasDraft),
    draftVersionNumber: raw.draftVersionNumber ?? null, employeesAssigned: raw.employeesAssigned ?? 0, typicalPayType: raw.typicalPayType ?? null,
    createdAtUtc: raw.createdAtUtc ?? null, updatedAtUtc: raw.updatedAtUtc ?? null,
    versionNumber: raw.contentVersionNumber ?? null, versionStatus: raw.contentVersionStatus ? jobRoleVersionStatusFromWire(raw.contentVersionStatus) : null,
    content: content(raw.content), draftContent: raw.draftContent ? content(raw.draftContent) : null,
    employerPayBand: mapPayBandSummary(raw.employerPayBand), benchmarkStatus: benchmarkStatusFromWire(raw.benchmarkStatus),
  };
}

export function mapVersionItem(raw: RawVersion): JobRoleVersionItem {
  return {
    id: raw.id, versionNumber: raw.versionNumber, status: jobRoleVersionStatusFromWire(raw.status), effectiveFromUtc: raw.effectiveFromUtc ?? null,
    effectiveToUtc: raw.effectiveToUtc ?? null, createdByUserId: raw.createdByUserId ?? null, createdAtUtc: raw.createdAtUtc ?? null,
    publishedAtUtc: raw.publishedAtUtc ?? null,
  };
}

export function mapVersionDetail(raw: RawVersion): JobRoleVersionDetail {
  return {
    ...mapVersionItem(raw),
    ...(raw.contentSchemaVersion !== undefined ? { contentSchemaVersion: raw.contentSchemaVersion } : {}),
    content: content(raw.content),
  };
}

export function mapTemplateDetail(raw: RawTemplateDetail): JobRoleTemplateDetail {
  return { ...raw, content: content(raw.content) };
}

export function mapEmployeePage(raw: { items: JobRoleEmployee[]; page: number; pageSize: number; totalCount: number }): Paged<JobRoleEmployee> {
  return {
    items: (raw.items ?? []).map((e) => ({ employeeId: e.employeeId, displayName: e.displayName, employeeNumber: e.employeeNumber, effectiveFrom: e.effectiveFrom ?? null })),
    page: raw.page, pageSize: raw.pageSize, totalCount: raw.totalCount,
  };
}

export function mapAuditPage(raw: { items: JobRoleAuditItem[]; page: number; pageSize: number; totalCount: number }): Paged<JobRoleAuditItem> {
  return {
    items: (raw.items ?? []).map((e) => ({
      id: e.id, occurredAtUtc: e.occurredAtUtc, actorUserId: e.actorUserId ?? null, actorEmailSnapshot: e.actorEmailSnapshot ?? null,
      eventType: e.eventType, metadataJson: e.metadataJson ?? null,
    })),
    page: raw.page, pageSize: raw.pageSize, totalCount: raw.totalCount,
  };
}

export function mapAssignment(raw: RawAssignment): EmployeeJobRoleAssignment {
  return {
    id: raw.id, jobTitleId: raw.jobTitleId, roleTitle: raw.roleTitle, roleCode: raw.roleCode ?? null, roleStatus: raw.roleStatus,
    jobRoleVersionId: raw.jobRoleVersionId ?? null, versionNumber: raw.versionNumber ?? null, isPrimary: Boolean(raw.isPrimary),
    effectiveFrom: raw.effectiveFrom, effectiveTo: raw.effectiveTo ?? null, assignedByUserId: raw.assignedByUserId ?? null, reason: raw.reason ?? null,
  };
}

// ---- PR 2: employer pay bands and market benchmarks (enums are STRINGS, money is a JSON number) ----

const num = (value: unknown): number => Number(value);

/** An unknown basis maps to null so a band is never shown with a wrong unit. */
export function payBasisFromWire(value: string | null | undefined): PayBasis | null {
  const name = (value ?? '').toLowerCase();
  return name === 'hourly' ? 'Hourly' : name === 'annual' ? 'Annual' : null;
}

/** A missing or unknown value maps to `NotPermitted`: the screen then shows no status and no number, never a guess. */
export function benchmarkStatusFromWire(value: string | null | undefined): BenchmarkStatus {
  const name = (value ?? '').toLowerCase();
  return name === 'available' ? 'Available' : name === 'nosoccode' ? 'NoSocCode' : name === 'nodata' ? 'NoData' : 'NotPermitted';
}

export function mapPayBandSummary(raw: RawPayBandSummary | null | undefined): EmployerPayBandSummary | null {
  const basis = payBasisFromWire(raw?.basis);
  if (!raw || !basis) return null;
  return {
    basis, minimum: num(raw.minimum), midpoint: num(raw.midpoint), maximum: num(raw.maximum),
    annualHoursAssumption: num(raw.annualHoursAssumption), concurrencyVersion: raw.concurrencyVersion,
  };
}

export interface RawPayBand {
  id: string; roleId: string; basis: string; currency: string; minimum: number; midpoint: number; maximum: number; annualHoursAssumption: number;
  effectiveFromDate: string; effectiveToDate: string | null; note: string | null; concurrencyVersion: number; createdAtUtc: string | null;
  acknowledgedAtUtc: string | null;
}

export function mapPayBand(raw: RawPayBand): PayBand | null {
  const basis = payBasisFromWire(raw.basis);
  if (!basis) return null;
  return {
    id: raw.id, roleId: raw.roleId, basis, currency: raw.currency ?? 'USD', minimum: num(raw.minimum), midpoint: num(raw.midpoint),
    maximum: num(raw.maximum), annualHoursAssumption: num(raw.annualHoursAssumption), effectiveFromDate: raw.effectiveFromDate,
    effectiveToDate: raw.effectiveToDate ?? null, note: raw.note ?? null, concurrencyVersion: raw.concurrencyVersion,
    createdAtUtc: raw.createdAtUtc ?? null, acknowledgedAtUtc: raw.acknowledgedAtUtc ?? null,
  };
}

/** `GET /pay-band` sends `{ band: {...} | null }`. A bare band is accepted too, so a gateway never fails on a harmless wrapper difference. */
export function mapPayBandEnvelope(raw: { band?: RawPayBand | null } | RawPayBand | null | undefined): PayBand | null {
  if (!raw) return null;
  const maybe = raw as { band?: RawPayBand | null; id?: string };
  if ('band' in maybe) return maybe.band ? mapPayBand(maybe.band) : null;
  return maybe.id ? mapPayBand(raw as RawPayBand) : null;
}

/** The history comes back newest first, as a list (an `items` or `bands` wrapper is tolerated). */
export function mapPayBandList(raw: RawPayBand[] | { items?: RawPayBand[]; bands?: RawPayBand[] } | null | undefined): PayBand[] {
  const list = Array.isArray(raw) ? raw : (raw?.items ?? raw?.bands ?? []);
  return list.map(mapPayBand).filter((band): band is PayBand => band !== null);
}

interface RawGeography { type: string; code: string | null; name: string }

const geographyTypeFromWire = (value: string): BenchmarkGeographyType => (
  value.toLowerCase() === 'metro' ? 'Metro' : value.toLowerCase() === 'state' ? 'State' : 'National'
);

const mapGeography = (raw: RawGeography): BenchmarkGeography => ({ type: geographyTypeFromWire(raw.type ?? ''), code: raw.code ?? null, name: raw.name ?? '' });

export interface RawBenchmarkSource {
  sourceCode: string; organization: string; title: string; url: string | null; kind: string; licenseStatus: string; attributionText: string | null;
  datasetEdition: string; referenceYear: number; retrievalDate: string; lastUpdatedUtc: string | null; expirationDate: string | null;
  requestedGeography: RawGeography; resolvedGeography: RawGeography; fallback: { applied: boolean; reason: string | null } | null;
  currency: string; originalBasis: string; convertedBasis: string;
  points: { label: string; percentile: number; originalValue: number; value: number }[] | null;
  percentileDefinition: string | null; sampleSize: number | null; reliabilityNote: string | null; dataAvailability: string;
}

export interface RawBenchmarkResult {
  roleId: string; socCode: string | null; status: string; message: string | null;
  request: { geographyType: string; state: string | null; metroCode: string | null; referenceYear: number | null; basis: string; annualHours: number; annualHoursSource: string };
  sources: RawBenchmarkSource[] | null; excluded: { sourceCode: string; reason: string }[] | null;
  suggestedRange: { status: string; reason: string | null } | null; limitations: string[] | null; disclaimer: string | null;
}

function mapBenchmarkSource(raw: RawBenchmarkSource): BenchmarkSourceResult {
  const original = payBasisFromWire(raw.originalBasis) ?? 'Annual';
  return {
    sourceCode: raw.sourceCode, organization: raw.organization, title: raw.title, url: raw.url ?? null, kind: raw.kind as BenchmarkSourceKind,
    licenseStatus: raw.licenseStatus as BenchmarkLicenseStatus, attributionText: raw.attributionText ?? null, datasetEdition: raw.datasetEdition,
    referenceYear: raw.referenceYear, retrievalDate: raw.retrievalDate, lastUpdatedUtc: raw.lastUpdatedUtc ?? null, expirationDate: raw.expirationDate ?? null,
    requestedGeography: mapGeography(raw.requestedGeography), resolvedGeography: mapGeography(raw.resolvedGeography),
    fallback: { applied: Boolean(raw.fallback?.applied), reason: raw.fallback?.reason ?? null }, currency: raw.currency ?? 'USD',
    originalBasis: original, convertedBasis: payBasisFromWire(raw.convertedBasis) ?? original,
    points: (raw.points ?? []).map((p) => ({ label: p.label, percentile: p.percentile, originalValue: num(p.originalValue), value: num(p.value) })),
    percentileDefinition: raw.percentileDefinition ?? null, sampleSize: raw.sampleSize ?? null, reliabilityNote: raw.reliabilityNote ?? null,
    dataAvailability: raw.dataAvailability === 'Full' ? 'Full' : 'Partial',
  };
}

/** A lookup never carries a number unless the server sent one in `sources`; an unknown status is treated as no data. */
export function mapBenchmarkResult(raw: RawBenchmarkResult): BenchmarkResult {
  const status = benchmarkStatusFromWire(raw.status);
  return {
    roleId: raw.roleId, socCode: raw.socCode ?? null, status: status === 'Available' || status === 'NoSocCode' ? status : 'NoData',
    message: raw.message ?? null,
    request: {
      geographyType: geographyTypeFromWire(raw.request?.geographyType ?? ''), state: raw.request?.state ?? null, metroCode: raw.request?.metroCode ?? null,
      referenceYear: raw.request?.referenceYear ?? null, basis: payBasisFromWire(raw.request?.basis) ?? 'Annual',
      annualHours: num(raw.request?.annualHours), annualHoursSource: raw.request?.annualHoursSource ?? 'default',
    },
    sources: (raw.sources ?? []).map(mapBenchmarkSource),
    excluded: (raw.excluded ?? []).map((e) => ({ sourceCode: e.sourceCode, reason: e.reason })),
    suggestedRange: { status: raw.suggestedRange?.status ?? 'Unavailable', reason: raw.suggestedRange?.reason ?? null },
    limitations: raw.limitations ?? [], disclaimer: raw.disclaimer ?? null,
  };
}

export interface RawBenchmarkRegistryItem {
  code: string; organization: string; title: string; url: string | null; kind: string; licenseStatus: string; isEnabled: boolean;
  attributionText: string | null; usageNotes: string | null;
  latestBatch: { datasetEdition: string; referenceYear: number; retrievalDate: string; importedAtUtc: string; rowCount: number; status: string } | null;
}

export function mapBenchmarkRegistry(raw: RawBenchmarkRegistryItem[] | null | undefined): BenchmarkSourceRegistryItem[] {
  return (raw ?? []).map((s) => ({
    code: s.code, organization: s.organization, title: s.title, url: s.url ?? null, kind: s.kind as BenchmarkSourceKind,
    licenseStatus: s.licenseStatus as BenchmarkLicenseStatus, isEnabled: Boolean(s.isEnabled), attributionText: s.attributionText ?? null,
    usageNotes: s.usageNotes ?? null, latestBatch: s.latestBatch ?? null,
  }));
}

// ---- guided fields (contract 2.1 to 2.5): occupations, reference options, role code suggestion, capabilities ----

export interface RawOccupationDataset {
  name: string; version: string; sourceOrganization: string; sourceUrl?: string | null; licenseName?: string | null; licenseUrl?: string | null;
  attributionText?: string | null; limitation?: string | null; retrievedOn?: string | null; isCurrent?: boolean; system?: string;
}

export interface RawOccupationItem {
  code: string; title: string; description?: string | null; majorGroupCode?: string | null; majorGroupName?: string | null; socCode?: string | null;
  matchedOn?: string | null; matchedText?: string | null;
}

export interface RawOccupationSearch {
  system: string; dataset: RawOccupationDataset | null; items: RawOccupationItem[] | null; totalMatches: number;
}

export const occupationSystemFromWire = (value: string | null | undefined): OccupationSystem => ((value ?? '').toLowerCase() === 'onet' ? 'Onet' : 'Soc');
export const occupationSystemToWire = (system: OccupationSystem): string => (system === 'Onet' ? 'Onet' : 'Soc');

export function mapOccupationDataset(raw: RawOccupationDataset | null | undefined): OccupationDataset | null {
  if (!raw) return null;
  return {
    name: raw.name, version: raw.version, sourceOrganization: raw.sourceOrganization, sourceUrl: raw.sourceUrl ?? null, licenseName: raw.licenseName ?? null,
    licenseUrl: raw.licenseUrl ?? null, attributionText: raw.attributionText ?? null, limitation: raw.limitation ?? null, retrievedOn: raw.retrievedOn ?? null,
    ...(raw.isCurrent !== undefined ? { isCurrent: Boolean(raw.isCurrent) } : {}),
    ...(raw.system ? { system: occupationSystemFromWire(raw.system) } : {}),
  };
}

export function mapOccupationItem(raw: RawOccupationItem): OccupationItem {
  const matched = (raw.matchedOn ?? '').toLowerCase();
  return {
    code: raw.code, title: raw.title, description: raw.description ?? null, majorGroupCode: raw.majorGroupCode ?? null, majorGroupName: raw.majorGroupName ?? null,
    socCode: raw.socCode ?? null, matchedOn: matched === 'code' ? 'Code' : matched === 'title' ? 'Title' : matched === 'synonym' ? 'Synonym' : null,
    matchedText: raw.matchedText ?? null,
  };
}

export function mapOccupationSearch(raw: RawOccupationSearch): OccupationSearchResult {
  return {
    system: occupationSystemFromWire(raw.system), dataset: mapOccupationDataset(raw.dataset), items: (raw.items ?? []).map(mapOccupationItem),
    totalMatches: raw.totalMatches ?? 0,
  };
}

export function mapOccupationDatasets(raw: RawOccupationDataset[] | null | undefined): OccupationDataset[] {
  return (raw ?? []).map((d) => mapOccupationDataset(d)).filter((d): d is OccupationDataset => d !== null);
}

export interface RawReferenceOption { code?: string | null; name: string; description?: string | null; group?: string | null; custom?: boolean; synonyms?: string[] | null }
export interface RawReferenceOptionList {
  category: string; datasetVersion?: string | null; groups?: { key: string; label: string; options?: RawReferenceOption[] | null }[] | null;
  totalStandard?: number; truncated?: boolean;
}

export function mapReferenceOption(raw: RawReferenceOption): ReferenceOption {
  return {
    code: raw.code ?? null, name: raw.name, description: raw.description ?? null, group: raw.group ?? null, custom: Boolean(raw.custom), synonyms: raw.synonyms ?? [],
  };
}

export function mapReferenceOptionList(raw: RawReferenceOptionList): ReferenceOptionList {
  return {
    category: raw.category as ReferenceCategory, datasetVersion: raw.datasetVersion ?? null,
    groups: (raw.groups ?? []).map((g) => ({ key: g.key, label: g.label, options: (g.options ?? []).map(mapReferenceOption) })).filter((g) => g.options.length > 0),
    totalStandard: raw.totalStandard ?? 0, truncated: Boolean(raw.truncated),
  };
}

export function mapCreateReferenceOption(raw: { option: RawReferenceOption; created: boolean }): CreateReferenceOptionResult {
  return { option: mapReferenceOption(raw.option), created: Boolean(raw.created) };
}

export function mapRoleCodeSuggestion(raw: { suggestion: string; proposedAvailable?: boolean | null }): RoleCodeSuggestion {
  return { suggestion: raw.suggestion, proposedAvailable: raw.proposedAvailable ?? null };
}

export function mapCapabilities(raw: Partial<JobRoleCapabilities> | null | undefined): JobRoleCapabilities {
  // A flag the server did not send is false: the UI never offers an action on a guess (the server still enforces it either way).
  return {
    canCreateRole: raw?.canCreateRole === true, canUpdateRole: raw?.canUpdateRole === true, canCreateDepartment: raw?.canCreateDepartment === true,
    canCreateReferenceOption: raw?.canCreateReferenceOption === true, canViewBenchmarks: raw?.canViewBenchmarks === true, canManagePayBand: raw?.canManagePayBand === true,
  };
}

/** The query strings of the guided-fields endpoints, built in one place so a test can pin them exactly. */
export function occupationSearchQuery(params: OccupationSearchParams): string {
  const q = new URLSearchParams();
  q.set('system', occupationSystemToWire(params.system));
  if (params.search) q.set('search', params.search);
  if (params.socCode) q.set('socCode', params.socCode);
  if (params.take !== undefined) q.set('take', String(params.take));
  return q.toString();
}

export function referenceOptionsQuery(params: ListReferenceOptionsParams): string {
  const q = new URLSearchParams();
  q.set('category', params.category);
  if (params.search) q.set('search', params.search);
  if (params.take !== undefined) q.set('take', String(params.take));
  if (params.templateId) q.set('templateId', params.templateId);
  return q.toString();
}

export function roleCodeSuggestionQuery(params: RoleCodeSuggestionParams): string {
  const q = new URLSearchParams();
  if (params.title) q.set('title', params.title);
  if (params.departmentId) q.set('departmentId', params.departmentId);
  if (params.jobFamily) q.set('jobFamily', params.jobFamily);
  if (params.careerLevel) q.set('careerLevel', params.careerLevel);
  if (params.proposed) q.set('proposed', params.proposed);
  if (params.excludeRoleId) q.set('excludeRoleId', params.excludeRoleId);
  return q.toString();
}
